import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(() => {
    try {
      const stored = localStorage.getItem("launch_favorites");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("launch_favorites", JSON.stringify(favorites));
    } catch {
      // Storage might be full or unavailable
    }
  }, [favorites]);

  const addFavorite = useCallback((launch) => {
    setFavorites((prev) => {
      if (prev.some((f) => f.id === launch.id)) return prev;
      return [...prev, {
        id: launch.id,
        name: launch.name,
        net: launch.net,
        rocket: launch.rocket,
        provider: launch.provider,
        pad_name: launch.pad_name,
        addedAt: new Date().toISOString()
      }];
    });
  }, []);

  const removeFavorite = useCallback((launchId) => {
    setFavorites((prev) => prev.filter((f) => f.id !== launchId));
  }, []);

  const isFavorite = useCallback((launchId) => {
    return favorites.some((f) => f.id === launchId);
  }, [favorites]);

  const toggleFavorite = useCallback((launch) => {
    if (isFavorite(launch.id)) {
      removeFavorite(launch.id);
      return false;
    } else {
      addFavorite(launch);
      return true;
    }
  }, [isFavorite, removeFavorite, addFavorite]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    favorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite
  }), [favorites, addFavorite, removeFavorite, isFavorite, toggleFavorite]);

  return (
    <FavoritesContext.Provider value={contextValue}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
};
